import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FileClock, BellRing, RefreshCw, ChevronDown } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DraftsPanel({ getAuthHeader }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nudging, setNudging] = useState(null);
  const [open, setOpen] = useState(false);

  const fetchDrafts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/application-drafts`, getAuthHeader());
      setDrafts(res.data);
    } catch { /* staff without access */ }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const nudge = async (userId) => {
    setNudging(userId);
    try {
      await axios.post(`${API_URL}/admin/application-drafts/${userId}/nudge`, {}, getAuthHeader());
      toast.success('Reminder sent to client');
      fetchDrafts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send nudge'); }
    finally { setNudging(null); }
  };

  if (!loading && drafts.length === 0) return null;

  return (
    <Card className="border-l-4 border-l-amber-400" data-testid="drafts-panel">
      <CardHeader
        className="py-3 flex flex-row items-center justify-between cursor-pointer select-none hover:bg-amber-50/40 transition-colors rounded-t-lg"
        onClick={() => setOpen(v => !v)} data-testid="drafts-panel-toggle">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <FileClock className="w-4 h-4 text-amber-600" /> Unfinished Applications
            <Badge className="bg-amber-500 text-white" data-testid="drafts-count">{drafts.length}</Badge>
          </CardTitle>
          {open && <CardDescription className="text-xs mt-1">Clients who saved an application but haven't submitted — follow up to convert them</CardDescription>}
        </div>
        <div className="flex items-center gap-1">
          {open && (
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); fetchDrafts(); }} data-testid="drafts-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          )}
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </CardHeader>
      {open && (
      <CardContent>
        {loading ? <p className="text-sm text-slate-400 py-3">Loading...</p> : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="drafts-table">
              <thead>
                <tr><th>Client</th><th>Contact</th><th>Amount</th><th>Progress</th><th>Last Saved</th><th>Reminded</th><th></th></tr>
              </thead>
              <tbody>
                {drafts.map(d => (
                  <tr key={d.user_id}>
                    <td className="font-medium">{d.client_name}</td>
                    <td className="text-xs text-slate-500">{d.phone}<span className="block">{d.email}</span></td>
                    <td className="tabular-nums">{d.loan_amount ? `R${Number(d.loan_amount).toLocaleString()}` : '—'}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-amber-500 rounded-full" style={{ width: `${d.progress_pct}%` }}></div>
                        </div>
                        <span className="text-xs text-slate-500">{d.step_label}</span>
                      </div>
                    </td>
                    <td className="text-xs text-slate-500">{d.updated_at ? new Date(d.updated_at).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                    <td>
                      {d.reminder_sent
                        ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 text-[10px]">Yes</Badge>
                        : <Badge variant="outline" className="bg-slate-50 text-slate-500 text-[10px]">No</Badge>}
                    </td>
                    <td>
                      <Button variant="outline" size="sm" className="text-xs" disabled={nudging === d.user_id}
                        onClick={() => nudge(d.user_id)} data-testid={`nudge-draft-${d.user_id}`}>
                        <BellRing className="w-3.5 h-3.5 mr-1" /> {nudging === d.user_id ? 'Sending...' : 'Send Nudge'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
}
